import { motion } from 'framer-motion';
import { LISTINGS } from './data';

// ESTATE FILE — two estate "wall plates" with floorplan thumb, photo strip, narrative.
// NOT a tab grid. Two big plates stacked vertically, each is a self-contained dossier.
const PLATES = [
  {
    listing: LISTINGS[0],
    rooms: [
      { lvl: 'Lower', items: ['Wine cellar (1,400 bottles)', 'Cinema (12 seats)', 'Pilates studio'] },
      { lvl: 'Main', items: ['Library', 'Formal dining', 'Conservatory'] },
      { lvl: 'Upper', items: ['7 en-suite bedrooms', 'Primary suite + dressing'] },
      { lvl: 'Grounds', items: ['60-ft pool', 'Tennis pavilion', 'Beach access'] },
    ],
    note: 'Built 1924 by McKim, Mead & White. Restored 2019 by architect Annabelle Selldorf. Records on file.',
    photos: ['https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg?auto=compress&cs=tinysrgb&w=900','https://images.pexels.com/photos/1457847/pexels-photo-1457847.jpeg?auto=compress&cs=tinysrgb&w=900','https://images.pexels.com/photos/210465/pexels-photo-210465.jpeg?auto=compress&cs=tinysrgb&w=900'],
  },
  {
    listing: LISTINGS[1],
    rooms: [
      { lvl: 'Lower', items: ['Spa & sauna', 'Gear room (ski-out)', 'Bourbon bar'] },
      { lvl: 'Main', items: ['Great room', 'Chef\u2019s kitchen', 'Two-storey hearth'] },
      { lvl: 'Upper', items: ['9 en-suite bedrooms', 'Loft office'] },
      { lvl: 'Grounds', items: ['Trout pond', 'Helipad', 'Equestrian barn (8 stalls)'] },
    ],
    note: 'Set 14 acres on Red Mountain. Designed by Bohlin Cywinski Jackson. Year-round access.',
    photos: ['https://images.pexels.com/photos/2079246/pexels-photo-2079246.jpeg?auto=compress&cs=tinysrgb&w=900','https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg?auto=compress&cs=tinysrgb&w=900','https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&w=900'],
  },
];

export default function Estate({ setPage }) {
  return (
    <div className="bg-[#F4EFE5] text-[#0E1B2D] min-h-screen">
      <section className="pt-32 pb-8 max-w-[1500px] mx-auto px-8 lg:px-14 border-b-2 border-[#1F3A5F]">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B5527] mb-3">— Office III / Estate file</p>
        <h1 className="font-serif italic text-[44px] lg:text-[88px] leading-[0.95] text-[#1F3A5F]">Two estates,<br />on the wall.</h1>
      </section>

      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 py-14 space-y-20">
        {PLATES.map((plate, idx) => (
          <motion.article key={plate.listing.ref} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white border border-[#1F3A5F]/20">
            {/* Plate masthead */}
            <header className="border-b-2 border-[#1F3A5F] px-6 lg:px-10 py-5 flex flex-wrap items-baseline justify-between gap-3 bg-[#FAF6EC]">
              <div>
                <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7B5527]">Ref {plate.listing.ref} · {plate.listing.region}</p>
                <h2 className="font-serif italic text-3xl lg:text-5xl text-[#1F3A5F] mt-1">{plate.listing.name}</h2>
              </div>
              <p className="font-serif text-2xl lg:text-3xl text-[#7B5527]">{plate.listing.ask}</p>
            </header>
            <div className="grid lg:grid-cols-[1.3fr_1fr] gap-0 lg:divide-x divide-[#1F3A5F]/15">
              {/* Left — Big photo + thumb strip */}
              <div className="p-0 lg:p-5">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={plate.listing.img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5 lg:mt-3">
                  {plate.photos.map((p, i) => (
                    <div key={i} className="aspect-[4/3] overflow-hidden">
                      <img src={p} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#1F3A5F]/65 leading-[1.7] mt-4 px-1.5 font-serif" dangerouslySetInnerHTML={{ __html: plate.note }} />
              </div>
              {/* Right — Floor schedule (NOT a floorplan image, but a typed accommodation table) */}
              <div className="p-6 lg:p-10 bg-[#FAF6EC]">
                <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7B5527] border-b-2 border-[#1F3A5F] pb-2">Schedule of accommodation</p>
                <div className="mt-5 divide-y divide-[#1F3A5F]/15">
                  {plate.rooms.map(r => (
                    <div key={r.lvl} className="py-3.5 grid grid-cols-[88px_1fr] gap-4 items-baseline">
                      <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#1F3A5F]">{r.lvl}</p>
                      <ul className="space-y-1 text-[#1F3A5F]/80 font-serif text-sm leading-[1.7]">
                        {r.items.map(it => <li key={it}>— {it}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
                <button onClick={() => setPage('viewing')} data-testid={`ml-plate-${plate.listing.ref}`} className="mt-7 inline-block bg-[#1F3A5F] text-[#F4EFE5] px-7 py-3 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#7B5527] transition-colors">Request {idx === 0 ? 'private viewing' : 'estate file'} →</button>
              </div>
            </div>
          </motion.article>
        ))}
      </section>
    </div>
  );
}
